import { memo, useCallback, useMemo } from 'react';
import { Clip, LanguageOption } from '@goldcast/player-config/src/types/clip-types';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import Button from '@/components/atoms/Button/Button';
import useTranslationState from '@/hooks/useTranslationState';

function CaptionLanguageSelectorPopup({
  onCaptionTranslation,
  clipData,
  closePopup
}: {
  onCaptionTranslation: (selectedLanguage: LanguageOption | null) => void;
  closePopup: () => void;
  clipData: Clip;
}) {
  const {
    availableLanguagesForTranslation,
    translatedLanguages,
    originalLanguage,
    selectedLanguage,
    isDisabled,
    handleLanguageChange,
    handleTranslation
  } = useTranslationState(clipData);

  const sections = useMemo(() => {
    return [
      ...(originalLanguage.length > 0
        ? [
            {
              label: '',
              options: originalLanguage
            }
          ]
        : []),
      ...(availableLanguagesForTranslation.length > 0
        ? [
            {
              label: 'Available Languages',
              options: availableLanguagesForTranslation
            }
          ]
        : []),
      ...(translatedLanguages.length > 0
        ? [
            {
              label: 'Translated Languages',
              options: translatedLanguages
            }
          ]
        : [])
    ];
  }, [originalLanguage, availableLanguagesForTranslation, translatedLanguages]);

  const onSelectLanguage = useCallback(
    (selected: string) => {
      handleLanguageChange(selected);
      if (!availableLanguagesForTranslation.some(lang => lang.name === selected)) {
        // if translation already exists, then close popup on selection (As it will get applied directly)
        closePopup();
      }
    },
    [handleLanguageChange, closePopup, availableLanguagesForTranslation]
  );

  return (
    <div className="flex max-h-[54vh] w-[23rem] flex-col rounded-lg border bg-white shadow">
      <div className="sticky top-0 z-10 flex-col items-center justify-between px-4 py-3">
        <div className="mb-4">
          <div className="text-lg font-medium">Translate</div>
          <div className="mt-1 text-sm text-slate-600">
            Select a language to translate your transcript and captions into.
          </div>
        </div>
        <div className="relative flex gap-2">
          <SelectDropdown
            selected={selectedLanguage?.name ?? ''}
            onChange={onSelectLanguage}
            menuClassName="fixed"
            placeholder="Select language"
            sections={sections}
          />
          <Button
            variation="filled"
            onClick={() => handleTranslation(selectedLanguage, onCaptionTranslation)}
            disabled={!selectedLanguage || isDisabled}
            trackingId="translate-button"
            type="button"
          >
            Translate
          </Button>
        </div>
      </div>
    </div>
  );
}

export default memo(CaptionLanguageSelectorPopup);
